import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import * as detectElementResize from 'javascript-detect-element-resize'; // `addResizeListener`/`removeResizeListener`
import * as _ from 'lodash';
import * as mapboxgl from 'mapbox-gl';
import {
  CameraOptions,
  FitBoundsOptions,
  LngLat,
  LngLatBounds,
  LngLatBoundsLike,
  Map,
  Style
} from 'mapbox-gl';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first } from 'rxjs/operators';

// `javascript-detect-element-resize` not module import friendly until https://github.com/sdecima/javascript-detect-element-resize/pull/52
// For now. Need to assign to something (`foo`) to add listener funcs to `window`. There's prob a prettier solution,
// but this works
const foo = detectElementResize;
const addResizeListener = (<any>window).addResizeListener;
const removeResizeListener = (<any>window).removeResizeListener;

export interface MapMoveEvent {
  userInitiated: boolean; // event originated from a user interaction with the map or map controls
  location: {
    bearing: number;
    bounds: LngLatBounds;
    center: LngLat;
    pitch: number;
    zoom: number;
  };
}

@Component({
  selector: 'lja-map',
  template: ``,
  styleUrls: [
    '../../../../../node_modules/@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css',
    '../../../../../node_modules/mapbox-gl/dist/mapbox-gl.css',
    './map.scss'
  ],
  encapsulation: ViewEncapsulation.None // needed for mapbox-gl styles
})
export class LJAMapComponent implements OnInit, OnDestroy, OnChanges {
  @HostBinding('class.lja-map')
  className = true;

  private _el;
  private _map$: BehaviorSubject<Map>;
  private _mapIfAvailable$: Observable<Map>; // emits once if map is available, otherwise completes
  private _rasterLayerIds: string[] = [];

  @Input()
  accessToken: string;
  @Input()
  attributionControl = true; // changes after map instantiation are not supported
  @Input()
  bounds: LngLatBoundsLike;
  @Input()
  fitBoundsOptions: FitBoundsOptions;
  @Input()
  cameraOptions: CameraOptions;
  @Input()
  style: Style | string = 'mapbox://styles/mapbox/streets-v9?optimize=true';
  @Input()
  autoResize: false;

  @Output()
  readonly mapMove = new EventEmitter<MapMoveEvent>();

  /**
   * Mapbox GL map component was resized
   */
  @Output()
  readonly mapResize = new EventEmitter();

  /** Subscribe to map load if you ever need the Mapbox `Map` control */
  @Output()
  get mapLoad(): Observable<Map> {
    return this._map$;
  }

  constructor(private _elementRef: ElementRef) {
    this._el = _elementRef.nativeElement;
    this._map$ = new BehaviorSubject<Map>(null);
    this._mapIfAvailable$ = this._map$.pipe(
      filter(m => !!m),
      first()
    );
  }

  ngOnInit() {
    _.extend(mapboxgl, { accessToken: this.accessToken }); // can't do `mapboxgl.accessToken = ...`, breaks build/rollup
    const map = new Map({
      ..._.omitBy(this.cameraOptions, _.isNil),
      container: this._el,
      style: this.style,
      attributionControl: this.attributionControl,
      trackResize: !this.autoResize // not needed if our own custom resize listener is on
      // renderWorldCopies: false // FIXME. bug when true and map is zoomed quickly a few times
    });

    map.on('load', () => this._map$.next(map)); // wait until map is loaded to ensure consumer has a valid map
    map.on('moveend', ({ userInitiated = true }) =>
      this.mapMove.emit({
        location: {
          bounds: map.getBounds(),
          center: map.getCenter(),
          zoom: map.getZoom(),
          bearing: map.getBearing(),
          pitch: map.getPitch()
        },
        userInitiated
      })
    );

    if (this.autoResize) {
      // Use `javascript-detect-element-resize` to detect resizes to the map container and resize Mapbox GL map
      addResizeListener(this._el, () => this.applyResize());
    }

    // this._fixRasterPerf(map); // TODO. This is valueable but compromises experience. Maybe enable behind an option.

    this.bounds &&
      // NOTE. never animates on initial fit bounds
      map.fitBounds(this.bounds, { ...(this.fitBoundsOptions || {}), animate: false }, <any>{
        userInitiated: false
      });
  }

  ngOnChanges({ bounds, cameraOptions, style }: SimpleChanges) {
    this._mapIfAvailable$.subscribe(map => {
      // order matters.. apply `bounds` after `cameraOptions`, bounds always wins
      style && map.setStyle(style.currentValue);
      cameraOptions &&
        cameraOptions.currentValue &&
        this._setCameraOptions(map, cameraOptions.currentValue);
      bounds && bounds.currentValue && this._setBounds(map, bounds.currentValue);
    });
  }

  ngOnDestroy() {
    this._mapIfAvailable$.subscribe(map => {
      map.remove();
      removeResizeListener(this._el, () => this.applyResize());
    });
  }

  applyResize() {
    this._mapIfAvailable$.subscribe(map => {
      map.resize();
      this.mapResize.emit();
    });
  }

  /**
   * Raster layer related performance helps.
   * Hide layer when zooming for less bandwidth consumption and less reloading jank.
   */
  private /* pure */ _fixRasterPerf(map: Map) {
    let rasterLayerIds = [];
    map.on('style.load', () => {
      rasterLayerIds = map
        .getStyle()
        .layers.filter(l => l.type === 'raster')
        .map(l => l.id);
    });
    map.on('zoomstart', () => {
      rasterLayerIds.forEach(l => this._setLayerVisibility(map, l, 'none'));
      map.once('zoomend', () => {
        rasterLayerIds.forEach(l => this._setLayerVisibility(map, l, 'visible'));
      });
    });
  }

  private /* pure */ _setLayerVisibility(
    map: Map,
    layerId: string,
    visibility: 'none' | 'visible'
  ) {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  }

  private /* pure */ _setCameraOptions(map: Map, cameraOptions: CameraOptions) {
    const { center, zoom, bearing, pitch } = cameraOptions;
    if (
      center === map.getCenter() &&
      zoom === map.getZoom() &&
      bearing === map.getBearing() &&
      pitch === map.getPitch()
    ) {
      return;
    }

    map.flyTo(cameraOptions, <any>{ userInitiated: false });
  }

  private /* pure */ _setBounds(map: Map, bounds: LngLatBoundsLike) {
    const curr = map.getBounds();
    const update = LngLatBounds.convert(bounds);
    if (curr.toString() !== update.toString()) {
      map.fitBounds(update, this.fitBoundsOptions, <any>{ userInitiated: false });
    }
  }
}
